import Loading from 'components/Loading';
import Colors from 'enums/Colors';
import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';

interface IModalAtualizarNomeUser {
    open: boolean;
    nomeAtual?: string;
    onClose: () => void;
    onSave: (nomeCompleto: string) => void;
    loading?: boolean;
}

const StyledModal = styled(Modal)`
    .modal-content {
        border: none;
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
        overflow: hidden;
    }

    .modal-header {
        background: ${Colors.Blue};
        border: none;
        padding: 24px;
    }

    .modal-title {
        font-size: 20px;
        font-weight: 600;
        color: white;
        margin: 0;
    }

    .modal-body {
        padding: 32px;
    }
`;

const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Label = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #333;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const Input = styled.input`
    padding: 12px 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 16px;
    font-family: inherit;
    transition: all 0.3s ease;

    &:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    &::placeholder {
        color: #999;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
`;

const NomeDisplayContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
    text-align: center;
`;

const NomeDisplay = styled.div`
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    padding: 32px;
    border-radius: 12px;
    width: 100%;
`;

const NomeLabel = styled.p`
    font-size: 12px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0 0 12px 0;
`;

const NomeText = styled.h3`
    font-size: 28px;
    font-weight: 700;
    color: #333;
    margin: 0;
    word-break: break-word;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
    padding: 10px 24px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    ${props =>
        props.variant === 'primary'
            ? `
        background: ${Colors.Blue};
        color: white;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }

        &:active {
            transform: translateY(0);
        }
    `
            : `
        background: #f5f5f5;
        color: #666;

        &:hover {
            background: #efefef;
        }

        &:active {
            background: #e5e5e5;
        }
    `}

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ModalAtualizarNomeUser = ({ open, nomeAtual = '', onClose, onSave, loading }: IModalAtualizarNomeUser) => {
    const [nomeCompleto, setNomeCompleto] = useState('');
    const [erro, setErro] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const valor = e.target.value;
        setNomeCompleto(valor);
        setErro('');
    };

    const handleSave = () => {
        const nomeTrimmed = nomeCompleto.trim();

        if (!nomeTrimmed) {
            setErro('Digite o nome completo');
            return;
        }

        if (nomeTrimmed.split(' ').length < 2) {
            setErro('Digite o nome completo (nome e sobrenome)');
            return;
        }

        onSave(nomeTrimmed);
        setNomeCompleto('');
        setErro('');
        setIsEditing(false);
    };

    const handleConfirm = () => {
        onSave(nomeAtual);
        setIsEditing(false);
        onClose();
    };

    const handleEdit = () => {
        setIsEditing(true);
        setNomeCompleto(nomeAtual);
        setErro('');
    };

    const handleClose = () => {
        setNomeCompleto('');
        setErro('');
        setIsEditing(false);
        onClose();
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSave();
        }
    };

    return (
        <StyledModal show={open} onHide={handleClose} centered backdrop="static" keyboard={false}>
            {loading ? (
                <>
                    <Modal.Header className="border-0" style={{ justifyContent: 'center', padding: '40px', color: Colors.White  }}>
                        <h1>Atualizando seu nome, por favor aguarde...</h1>
                    </Modal.Header>
                    <Modal.Body className="border-0" style={{ textAlign: 'center', paddingBottom: '40px' }}>
                        <Loading />
                    </Modal.Body>
                </>
            ) : (
                <>
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title>SEU NOME ESTÁ CORRETO?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="border-0">
                        {!isEditing ? (
                            <NomeDisplayContainer>
                                <p style={{ lineHeight: '20px' }}>
                                    Por favor, <strong> confirme se o seu nome está correto para emissão do certificado.</strong> Caso contrário,
                                    clique em <strong>Corrigir</strong> para atualizar.
                                </p>
                                <NomeDisplay>
                                    <NomeLabel>Nome Cadastrado</NomeLabel>
                                    <NomeText>{nomeAtual || 'Não informado'}</NomeText>
                                </NomeDisplay>
                                <ButtonContainer>
                                    <Button variant="secondary" onClick={handleClose}>
                                        Cancelar
                                    </Button>
                                    <Button variant="secondary" onClick={handleEdit}>
                                        Corrigir
                                    </Button>
                                    <Button variant="primary" onClick={handleConfirm}>
                                        Confirmar
                                    </Button>
                                </ButtonContainer>
                            </NomeDisplayContainer>
                        ) : (
                            <FormContainer>
                                <FormGroup>
                                    <Label htmlFor="nomeCompleto">Nome Completo</Label>
                                    <Input
                                        id="nomeCompleto"
                                        type="text"
                                        placeholder="Digite seu nome completo"
                                        value={nomeCompleto}
                                        onChange={handleInputChange}
                                        onKeyPress={handleKeyPress}
                                        autoFocus
                                    />
                                    {erro && <span style={{ color: '#ef4444', fontSize: '13px', fontWeight: '500' }}>{erro}</span>}
                                </FormGroup>
                            </FormContainer>
                        )}
                    </Modal.Body>
                    {isEditing && (
                        <Modal.Footer className="border-0" style={{ padding: '0 32px 32px 32px' }}>
                            <ButtonContainer>
                                <Button variant="secondary" onClick={() => setIsEditing(false)}>
                                    Cancelar
                                </Button>
                                <Button variant="primary" onClick={handleSave} disabled={!nomeCompleto.trim()}>
                                    Salvar
                                </Button>
                            </ButtonContainer>
                        </Modal.Footer>
                    )}
                </>
            )}
        </StyledModal>
    );
};

export default ModalAtualizarNomeUser;
